import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@deepguide-ai/ui/form'
import { Input } from '@deepguide-ai/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { queryClient } from '@/lib/trpc'
import { trpc } from '@/lib/trpc'
import { workspaceIdAtom } from '@/stores/workspaceStore'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { IntegrationDTO, LocalFileIntegrationMetadata } from '@deepguide-ai/types'
import { useAtom } from 'jotai'
import { FolderOpen, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod/v3';

const LocalFileIntegrationDocTypeSchema = z.enum(['docusaurus', 'vitepress', 'gitbook', 'markdown'])
const LocalFileIntegrationDocType = LocalFileIntegrationDocTypeSchema.enum

// Define the schema for the form
const formSchema = z.object({
  path: z.string().min(1, 'Path is required'),
  docType: LocalFileIntegrationDocTypeSchema,
  baseUrl: z.string().optional(),
})

// Define the props for the component
export interface LocalFileConfigModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  integration?: IntegrationDTO
}

export function LocalFileConfigModal({
  open,
  onOpenChange,
  integration,
}: LocalFileConfigModalProps) {
  const [submitting, setSubmitting] = useState(false)
  const [workspaceId] = useAtom(workspaceIdAtom)

  const integrationMetadata = integration?.metadata as LocalFileIntegrationMetadata
  // Setup form with validation
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      path: integrationMetadata?.path || '',
      docType: integrationMetadata?.docType || LocalFileIntegrationDocType.docusaurus,
      baseUrl: integrationMetadata?.baseUrl || '',
    },
  })

  // Get mutations
  const updateIntegration = useMutation(
    trpc.integrations.updateIntegration.mutationOptions()
  )

  // Handle form submission
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!workspaceId || !integration) return

    try {
      setSubmitting(true)

      // Update the integration with the new configuration
      await updateIntegration.mutateAsync({
        id: integration.id,
        metadata: {
          ...integrationMetadata,
          path: values.path,
          docType: values.docType,
          baseUrl: values.baseUrl,
          isConfigured: true,
          features: {
            [values.docType]: true,
          },
        },
      })

      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })

      // Close the modal
      onOpenChange(false)
    } catch (error) {
      console.error('Error updating local documentation configuration:', error)
    } finally {
      setSubmitting(false)
    }
  }

  // Handle folder selection
  const handleSelectFolder = async () => {
    if (!window._main_api) {
      console.error('Electron API not available')
      return
    }

    try {
      const result = await window._main_api.openDirectory()
      if (result && result.path) {
        form.setValue('path', result.path)
      }
    } catch (error) {
      console.error('Error selecting folder:', error)
    }
  }

  const docTypes = [
    { value: 'docusaurus', label: 'Docusaurus' },
    { value: 'vitepress', label: 'VitePress' },
    { value: 'gitbook', label: 'GitBook' },
    { value: 'markdown', label: 'Markdown/MDX Files' },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>
            {integrationMetadata?.isConfigured
              ? 'Update Local Documentation'
              : 'Configure Local Documentation'}
          </DialogTitle>
          <DialogDescription>
            Select a local folder containing your documentation.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='path'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Documentation Path</FormLabel>
                  <div className='flex gap-2'>
                    <FormControl>
                      <Input placeholder='/path/to/docs' {...field} />
                    </FormControl>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={handleSelectFolder}
                    >
                      <FolderOpen className='mr-2 h-4 w-4' />
                      Browse
                    </Button>
                  </div>
                  <FormDescription>
                    Path to your local documentation folder.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='docType'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Documentation Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder='Select documentation type' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {docTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Select the type of documentation you're using.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='baseUrl'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Base URL (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder='/docs' {...field} />
                  </FormControl>
                  <FormDescription>
                    Base URL path for your documentation, if applicable.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={submitting}>
                {submitting && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {integrationMetadata?.isConfigured ? 'Update' : 'Configure'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
